import {
  ComputeInputPropsFn,
  ComputeInputPropsFnParams,
} from '@/features/InvoiceReport/Edit/components/InvoiceTransaction/Columns/useColumns';
import {
  FormInputNumber,
  FormInputNumberProps,
  InputNumberProps,
} from '@moneyforward/ap-frontend-components';
import { memo, useMemo } from 'react';
import { FieldValues } from 'react-hook-form';
import { CellProps } from './type';

export type Props<TFieldValues extends FieldValues> = {
  computeInputProps?: ComputeInputPropsFn;
  computeInputPropsParams?: ComputeInputPropsFnParams;
  format?: InputNumberProps['format'];
} & FormInputNumberProps<TFieldValues> &
  CellProps;

export const callComputeInputProps = (
  fn: ComputeInputPropsFn,
  params: ComputeInputPropsFnParams
): object => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  return (fn as (...args: (string | boolean | undefined)[]) => object)(
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    ...(params as [
      string | boolean | undefined,
      (string | boolean | undefined)?
    ])
  );
};

const InnerFormInputNumberCell = <TFieldValues extends FieldValues>({
  control,
  name,
  computeInputProps,
  computeInputPropsParams,
  format,
  ...rest
}: Props<TFieldValues>) => {
  const memorizedInputProps = useMemo(
    () => ({
      ...(computeInputProps && computeInputPropsParams
        ? callComputeInputProps(computeInputProps, computeInputPropsParams)
        : {}),
      format,
    }),
    [computeInputProps, computeInputPropsParams, format]
  );
  return (
    <FormInputNumber<TFieldValues>
      control={control}
      name={name}
      inputProps={memorizedInputProps}
      {...rest}
    />
  );
};
InnerFormInputNumberCell.displayName = 'FormInputNumberCell';

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const FormInputNumberCell = memo(
  InnerFormInputNumberCell
) as typeof InnerFormInputNumberCell;
